import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '@/api/errors';
import {
  getPhotoMailDraft,
  sendPhotoMail,
  checkPhotoMail,
  markPhotoMailNotSent,
  repairPhotoMailNotes,
  type PhotoMailDraft,
  type PhotoMailSend,
} from '@/api/vorderingen';

/**
 * Where the prompt was opened. From the wizard, the person already chose the
 * signer: only that choice prefills (a picked contact without email, or a typed
 * name, starts EMPTY). From a card, nothing was chosen, so the server's signer
 * (the Facturatie task's "Ondertekenaar:" matched to one contact) prefills.
 */
export type PhotoMailOpener =
  | { from: 'wizard'; email: string | null; contactPicked: boolean }
  | { from: 'card' };

interface PhotoMailPromptProps {
  vorderingId: number;
  opener: PhotoMailOpener;
  onClose: () => void;
  /** After a send whose outcome is known or unknown, so the card can refresh its sent lines. */
  onChanged?: () => void;
}

const EMAIL_RE = /^[^\s@<>]+@[^\s@<>]+\.[A-Za-z]{2,}$/;
const fmtDate = (d: string) => {
  const t = Date.parse(d.includes('T') ? d : `${d.replace(' ', 'T')}Z`);
  return Number.isNaN(t) ? d : new Date(t).toLocaleDateString('nl-BE');
};

type Outcome =
  | { kind: 'idle' }
  | { kind: 'sent'; noteFailed: boolean; noTask: boolean; followFailed: boolean }
  | { kind: 'unknown' }
  | { kind: 'error'; message: string };

interface Chip { email: string; label: string; source: 'ondertekenaar' | 'klantnotitie' | 'manueel' }

/**
 * "Foto's versturen": the photo report to the customer, right after the
 * vordering is made or later from its card. One recipient field (prefilled
 * from the Ondertekenaar), more can be added; the text is editable, the links
 * are always added by the server; a preview before Send.
 *
 * A send whose outcome is not known (timeout, lost connection) is shown as
 * "status onbekend", never as "niet verstuurd": the server keeps it blocked
 * until Odoo shows the message, or a person marks it not sent after checking.
 */
export function PhotoMailPrompt({ vorderingId, opener, onClose, onChanged }: PhotoMailPromptProps) {
  const [draft, setDraft] = useState<PhotoMailDraft | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [chips, setChips] = useState<Chip[]>([]);
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [legacyChecked, setLegacyChecked] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });
  const [sendNote, setSendNote] = useState<string | null>(null);
  const initialised = useRef(false);

  const load = async () => {
    try {
      const d = await getPhotoMailDraft(vorderingId);
      setDraft(d);
      setLoadError(null);
      if (!initialised.current) {
        initialised.current = true;
        const first: Chip[] = [];
        const add = (c: Chip) => { if (c.email && !first.some((x) => x.email === c.email)) first.push(c); };
        if (opener.from === 'wizard') {
          // Never the server's guess: it could be ANOTHER contact with the same name.
          if (opener.email) add({ email: opener.email.trim().toLowerCase(), label: opener.email, source: 'ondertekenaar' });
        } else if (d.signer) {
          add({ email: d.signer.email, label: `${d.signer.name} <${d.signer.email}>`, source: 'ondertekenaar' });
        }
        for (const s of d.suggestions) add({ email: s.email, label: `${s.name === s.email ? s.email : `${s.name} <${s.email}>`}`, source: 'klantnotitie' });
        setChips(first);
        setText(d.default_text);
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Laden mislukt');
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [vorderingId]);

  const openSends = useMemo(() => (draft?.sends ?? []).filter((s) => s.state !== 'sent'), [draft]);
  const sentTo = useMemo(() => new Set((draft?.sends ?? []).filter((s) => s.state === 'sent').flatMap((s) => s.recipients.map((r) => r.email.toLowerCase()))), [draft]);

  const addTyped = () => {
    const e = typed.trim().toLowerCase();
    if (!e) return;
    if (!EMAIL_RE.test(e)) { setTypedError('Geen geldig e-mailadres.'); return; }
    if (chips.some((c) => c.email === e)) { setTyped(''); return; }
    setChips([...chips, { email: e, label: e, source: 'manueel' }]);
    setTyped('');
    setTypedError(null);
  };

  const addContact = (id: string) => {
    const c = draft?.contacts.find((x) => String(x.id) === id);
    if (c && !chips.some((x) => x.email === c.email)) setChips([...chips, { email: c.email, label: `${c.name} <${c.email}>`, source: 'manueel' }]);
  };

  const blockers: string[] = [];
  if (draft?.sender_error) blockers.push(draft.sender_error);
  if (draft?.links_error) blockers.push(draft.links_error);
  if (draft && !draft.legacy_checkable && !legacyChecked) blockers.push('Vink aan dat je eerdere verzendingen gecontroleerd hebt.');
  if (!chips.length) blockers.push('Geef minstens één ontvanger.');
  const canSend = !!draft && !sending && blockers.length === 0 && outcome.kind !== 'sent' && outcome.kind !== 'unknown';

  const onSend = async () => {
    if (!canSend) return;
    setSending(true);
    setSendNote(null);
    try {
      const r = await sendPhotoMail(vorderingId, { recipients: chips.map((c) => c.email), text, legacy_checked: legacyChecked || undefined });
      if (r.status === 'sent') setOutcome({ kind: 'sent', noteFailed: r.taskNote === 'failed', noTask: r.taskNote === 'no-task', followFailed: r.follow === 'failed' });
      else setOutcome({ kind: 'unknown' });
    } catch (err) {
      // A refusal from the server (4xx with a code; Odoo's own refusals are
      // 424) is definite; a lost connection or any server error may have
      // gone through, even after the mail left: unknown.
      const definite = err instanceof ApiError && err.code !== 'NETWORK_INTERRUPTED'
        && (err.status >= 400 && err.status < 500);
      if (definite) {
        setOutcome({ kind: 'error', message: err.message });
      } else {
        setOutcome({ kind: 'unknown' });
      }
    } finally {
      setSending(false);
      onChanged?.();
      void load();
    }
  };

  const onCheck = async (s: PhotoMailSend) => {
    if (!s.sendId) return;
    try {
      const r = await checkPhotoMail(vorderingId, s.sendId);
      setSendNote(r.state === 'sent' ? 'Gevonden in Odoo: de foto\'s zijn verstuurd.' : 'Nog niet gevonden in Odoo — status blijft onbekend.');
      // Established: the prompt's own outcome follows, so it no longer says "status onbekend".
      if (r.state === 'sent') setOutcome({ kind: 'sent', noteFailed: r.taskNote === 'failed', noTask: r.taskNote === 'no-task', followFailed: false });
    } catch (err) {
      setSendNote(err instanceof Error ? err.message : 'Controleren mislukt');
    }
    onChanged?.();
    void load();
  };

  const onNotSent = async (s: PhotoMailSend) => {
    if (!s.sendId) return;
    try {
      const r = await markPhotoMailNotSent(vorderingId, s.sendId);
      setSendNote(r.result === 'was-sent' ? 'Toch gevonden in Odoo: de foto\'s zijn verstuurd.' : 'Gemarkeerd als niet verstuurd. Je kan opnieuw versturen.');
      if (r.result === 'ok') setOutcome({ kind: 'idle' });
      if (r.result === 'was-sent') setOutcome({ kind: 'sent', noteFailed: r.taskNote === 'failed', noTask: r.taskNote === 'no-task', followFailed: false });
    } catch (err) {
      setSendNote(err instanceof Error ? err.message : 'Mislukt');
    }
    onChanged?.();
    void load();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="photo-mail-prompt-title"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="border-b border-gray-100 px-5 py-4">
          <h2 id="photo-mail-prompt-title" className="text-base font-bold text-td-navy">Foto&apos;s versturen naar de klant</h2>
          {draft && <p className="mt-1 text-xs text-td-muted">Bericht op verkooporder {draft.order.name}, van jou, alleen aan de ontvangers hieronder.</p>}
        </div>

        <div className="space-y-4 overflow-y-auto px-5 py-4 text-sm">
          {loadError && <p className="text-td-magenta" role="alert">{loadError}</p>}
          {!draft && !loadError && <p className="text-td-muted">Laden…</p>}

          {draft && (
            <>
              {(draft.sends.length > 0) && (
                <div className="rounded border border-gray-200 bg-gray-50 p-2 text-xs" aria-label="Eerdere verzendingen">
                  {draft.sends.map((s, i) => (
                    <div key={s.sendId ?? `odoo-${i}`} className="flex flex-wrap items-center gap-2 py-0.5">
                      {s.state === 'sent' ? (
                        <span className="text-td-navy">Foto&apos;s verstuurd naar {s.recipients.map((r) => `${r.name}${r.email && r.email !== r.name ? ` <${r.email}>` : ''}`).join(', ')} op {fmtDate(s.date)}{s.by ? ` door ${s.by}` : ''}</span>
                      ) : (
                        <>
                          <span className="font-semibold text-td-magenta">Status onbekend</span>
                          <span className="text-td-muted">({s.recipients.map((r) => r.email).join(', ')}, {fmtDate(s.date)})</span>
                          <button type="button" className="font-semibold text-td-blue hover:underline" onClick={() => void onCheck(s)}>Controleer</button>
                          <button type="button" className="font-semibold text-td-muted hover:underline" onClick={() => void onNotSent(s)}>Markeer als niet verstuurd</button>
                        </>
                      )}
                    </div>
                  ))}
                  {openSends.length > 0 && (
                    <p className="mt-1 text-td-muted">
                      Kijk eerst in verkooporder <strong>{draft.order.name}</strong> in Odoo. &quot;Niet verstuurd&quot; kan pas 15 minuten na de verzending.
                    </p>
                  )}
                </div>
              )}
              {sendNote && <p className="text-xs text-td-navy" role="status">{sendNote}</p>}

              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Aan</p>
                <div className="mt-1 flex flex-wrap gap-1" aria-label="Ontvangers">
                  {chips.map((c) => (
                    <span key={c.email} className="inline-flex items-center gap-1 rounded-full bg-td-blue/10 px-2 py-0.5 text-xs text-td-navy">
                      {c.label}
                      {c.source === 'klantnotitie' && <span className="text-td-muted">(uit klantnotitie)</span>}
                      {sentTo.has(c.email) && <span className="text-td-magenta">(al verstuurd)</span>}
                      <button type="button" aria-label={`Verwijder ${c.email}`} className="text-td-muted hover:text-td-magenta" onClick={() => setChips(chips.filter((x) => x.email !== c.email))}>×</button>
                    </span>
                  ))}
                  {!chips.length && <span className="text-xs text-td-light">Nog geen ontvanger.</span>}
                </div>
                {opener.from === 'card' && draft.signer_note && <p className="mt-1 text-[11px] text-td-muted">{draft.signer_note}</p>}
                {opener.from === 'wizard' && opener.contactPicked && !opener.email && (
                  <p className="mt-1 text-[11px] text-td-muted">Het gekozen ondertekenaar-contact heeft geen e-mailadres — typ er een of kies een contact.</p>
                )}
                {draft.rule_misses.length > 0 && <p className="mt-1 text-[11px] text-td-muted">Klantnotitie noemt {draft.rule_misses.join(', ')}: niet gevonden als contact met e-mail.</p>}
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <input
                    type="email"
                    aria-label="E-mailadres toevoegen"
                    value={typed}
                    onChange={(e) => { setTyped(e.target.value); setTypedError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTyped(); } }}
                    placeholder="e-mailadres"
                    className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm text-td-navy focus:border-td-blue focus:outline-none focus:ring-1 focus:ring-td-blue"
                  />
                  <button type="button" onClick={addTyped} className="rounded border border-gray-300 px-3 py-1.5 text-sm font-semibold text-td-navy hover:bg-gray-50">+ Toevoegen</button>
                  {draft.contacts.length > 0 && (
                    <select aria-label="Kies contact" value="" onChange={(e) => addContact(e.target.value)} className="rounded border border-gray-300 px-2 py-1.5 text-sm text-td-muted sm:w-56">
                      <option value="">Kies contact…</option>
                      {draft.contacts.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.email}</option>)}
                    </select>
                  )}
                </div>
                {typedError && <p className="mt-1 text-[11px] text-td-magenta">{typedError}</p>}
              </div>

              <div>
                <label htmlFor="photo-mail-text" className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Tekst</label>
                <textarea
                  id="photo-mail-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={5}
                  maxLength={5000}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-td-navy focus:border-td-blue focus:outline-none focus:ring-1 focus:ring-td-blue"
                />
                <p className="text-[11px] text-td-light">De links naar de foto&apos;s komen er altijd onder; Odoo voegt je handtekening toe bij het verzenden.</p>
              </div>

              {!draft.legacy_checkable && (
                <label className="flex items-start gap-2 rounded border border-td-magenta/30 bg-td-magenta/5 p-2 text-xs text-td-navy">
                  <input type="checkbox" checked={legacyChecked} onChange={(e) => setLegacyChecked(e.target.checked)} />
                  <span>
                    {draft.task_status === 'ambiguous' ? 'De Facturatie-taak is niet eenduidig te vinden' : 'Facturatie-taak niet gevonden'}: eerdere verzendingen zijn niet te controleren en er komt geen notitie op een taak.
                    {' '}Kijk in de verkooporder en vink aan: <strong>eerdere verzending gecontroleerd</strong>.
                  </span>
                </label>
              )}

              <div className="rounded-lg border border-gray-200 p-3 text-xs" aria-label="Voorbeeld">
                <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Voorbeeld</p>
                <p className="mt-1"><span className="text-td-muted">Van:</span> {draft.sender ? `${draft.sender.name} <${draft.sender.email}>` : '—'}</p>
                <p><span className="text-td-muted">Aan:</span> {chips.map((c) => c.email).join(', ') || '—'}</p>
                <p><span className="text-td-muted">Onderwerp:</span> {draft.subject}</p>
                <div className="mt-2 whitespace-pre-wrap text-td-navy">{text}</div>
                <p className="mt-2 text-td-navy">
                  Foto rapport (PDF): {draft.links?.pdf ?? (draft.links?.made_on_send ? 'een nieuwe link, gemaakt bij Versturen' : '—')}
                  {draft.links?.zip && <><br />Losse foto&apos;s (ZIP): {draft.links.zip}</>}
                  {!draft.links?.zip && draft.links?.zip_made_on_send && <><br />Losse foto&apos;s (ZIP): een nieuwe link, gemaakt bij Versturen</>}
                </p>
                {draft.sender?.signature_text
                  ? <>
                      <p className="mt-2 text-[11px] text-td-light">Odoo voegt je handtekening toe bij het verzenden:</p>
                      <div className="whitespace-pre-wrap text-td-muted" aria-label="Handtekening">{`-- \n${draft.sender.signature_text}`}</div>
                    </>
                  : <p className="mt-2 text-td-light">(geen handtekening in Odoo)</p>}
              </div>

              {blockers.length > 0 && outcome.kind === 'idle' && (
                <ul className="list-disc pl-4 text-xs text-td-muted">{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
              )}
              {outcome.kind === 'sent' && (
                <p className="text-sm font-semibold text-td-green" role="status">
                  Verstuurd.
                  {outcome.noteFailed && <span className="font-normal text-td-magenta"> Verstuurd — notitie op de Facturatie-taak mislukt. Herstel het via de vordering.</span>}
                  {outcome.followFailed && <span className="font-normal text-td-magenta"> Verstuurd — volgen van de verkooporder mislukt.</span>}
                  {outcome.noTask && <span className="font-normal text-td-muted"> Geen Facturatie-taak gevonden, dus geen notitie.</span>}
                </p>
              )}
              {outcome.kind === 'unknown' && (
                <p className="text-sm font-semibold text-td-magenta" role="status">
                  Status onbekend — we controleren dit. Verstuur niet opnieuw; gebruik &quot;Controleer&quot; hierboven.
                </p>
              )}
              {outcome.kind === 'error' && <p className="text-sm text-td-magenta" role="alert">{outcome.message}</p>}
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
          {/* While a send is in flight closing would not stop it, so the button cannot promise "Niet versturen". */}
          <button type="button" onClick={onClose} disabled={sending} className="rounded-lg px-3 py-2 text-sm font-semibold text-td-muted hover:bg-gray-100 disabled:opacity-40">
            {sending ? 'Bezig met versturen…' : outcome.kind === 'sent' || outcome.kind === 'unknown' ? 'Sluiten' : 'Niet versturen'}
          </button>
          <button
            type="button"
            onClick={() => void onSend()}
            disabled={!canSend}
            className="rounded-lg bg-td-blue px-4 py-2 text-sm font-bold text-white hover:bg-td-blue/90 disabled:opacity-40"
          >
            {sending ? 'Versturen…' : 'Versturen'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The photo-mail lines on an opened vordering card: who got the photos when,
 * sends with "status onbekend", a missing Facturatie note ("Herstel" adds it,
 * never resends the mail), or "Facturatie-taak niet gevonden". Loaded when the
 * card opens: one call for that card, not one per card in the list.
 */
export function PhotoMailCardLines({ vorderingId, refreshKey, canWrite }: { vorderingId: number; refreshKey: number; canWrite: boolean }) {
  const [draft, setDraft] = useState<PhotoMailDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [repairing, setRepairing] = useState(false);
  const [repairNote, setRepairNote] = useState<string | null>(null);

  const load = async () => {
    try {
      setDraft(await getPhotoMailDraft(vorderingId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [vorderingId, refreshKey]);

  const onRepair = async () => {
    setRepairing(true);
    try {
      const r = await repairPhotoMailNotes(vorderingId);
      setRepairNote(r.task ? `Notitie hersteld (${r.written}).` : 'Facturatie-taak niet gevonden.');
    } catch (err) {
      setRepairNote(err instanceof Error ? err.message : 'Herstellen mislukt');
    } finally {
      setRepairing(false);
      void load();
    }
  };

  if (error) return <p className="text-[11px] text-td-light">Foto-mail: {error}</p>;
  if (!draft) return <p className="text-[11px] text-td-light">Foto-mail laden…</p>;
  // 'failed', or never confirmed (a send found in Odoo afterwards: its note was never tried). Repair posts the note only.
  const noteMissing = draft.task_status === 'matched'
    && draft.sends.some((s) => s.state === 'sent' && s.sendId && (s.taskNote === 'failed' || s.taskNote === null));
  return (
    <div aria-label="Foto-mail">
      <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted">Foto&apos;s naar de klant</p>
      {draft.sends.length === 0 && <p className="mt-1 text-[11px] text-td-light">Nog niet verstuurd.</p>}
      {draft.sends.map((s, i) => (
        <p key={s.sendId ?? `odoo-${i}`} className={`mt-1 text-[11px] ${s.state === 'sent' ? 'text-td-navy' : 'font-semibold text-td-magenta'}`}>
          {s.state === 'sent'
            ? `Foto's verstuurd naar ${s.recipients.map((r) => `${r.name}${r.email && r.email !== r.name ? ` <${r.email}>` : ''}`).join(', ')} op ${fmtDate(s.date)}${s.by ? ` door ${s.by}` : ''}`
            : `Status onbekend: ${s.recipients.map((r) => r.email).join(', ')} (${fmtDate(s.date)}) — open "Foto's versturen" om te controleren`}
        </p>
      ))}
      {draft.task_status !== 'matched' && <p className="mt-1 text-[11px] text-td-muted">Facturatie-taak niet gevonden.</p>}
      {noteMissing && (
        <p className="mt-1 text-[11px] text-td-magenta">
          Notitie ontbreekt op de Facturatie-taak.
          {canWrite && (
            <button type="button" disabled={repairing} onClick={() => void onRepair()} className="ml-2 font-semibold text-td-blue hover:underline disabled:opacity-40">Herstel</button>
          )}
        </p>
      )}
      {repairNote && <p className="mt-1 text-[11px] text-td-navy" role="status">{repairNote}</p>}
    </div>
  );
}
